import React from "react";
import { api, formatApiError, API as API_BASE } from "../lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import {
    Download,
    Copy,
    Check,
    Megaphone,
    FileVideo,
    Beaker,
    TrendingUp,
    Grid3x3,
    List,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = API_BASE.replace(/\/api$/, "");

function CopyField({ label, value, testId }) {
    const [copied, setCopied] = React.useState(false);
    const onCopy = () => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
    };
    return (
        <div className="flex items-center gap-2 surface-soft rounded-xl p-3" data-testid={testId}>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">
                    {label}
                </div>
                <div className="font-mono text-xs text-slate-800 truncate">{value}</div>
            </div>
            <button
                onClick={onCopy}
                className="btn-pill-ghost text-xs px-3 py-1.5 inline-flex items-center"
                data-testid={`${testId}-copy`}
            >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
        </div>
    );
}

function AdKitDialog({ video, onClose }) {
    const [kit, setKit] = React.useState(null);
    const [eff, setEff] = React.useState(null);
    const [testRevenue, setTestRevenue] = React.useState(1000);
    const [testResult, setTestResult] = React.useState(null);
    const [testing, setTesting] = React.useState(false);

    React.useEffect(() => {
        if (!video) return;
        let mounted = true;
        Promise.all([
            api.get(`/videos/${video.id}/ad-kit`),
            api.get(`/brand/payout-rules/effective/${video.id}`),
        ]).then(([k, e]) => {
            if (!mounted) return;
            setKit(k.data);
            setEff(e.data);
        });
        return () => {
            mounted = false;
        };
    }, [video]);

    const runTest = async () => {
        setTesting(true);
        try {
            const { data } = await api.post(`/videos/${video.id}/test-attribution`, {
                revenue: parseFloat(testRevenue) || 0,
            });
            setTestResult(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setTesting(false);
        }
    };

    if (!video) return null;
    const utmString = kit
        ? `?utm_source=${kit.utm_source}&utm_medium=${kit.utm_medium}&utm_campaign=${kit.utm_campaign}&utm_content=${kit.utm_content}`
        : "";

    const ruleLabel = (() => {
        if (!eff?.rule) return "No payout terms set yet";
        const r = eff.rule;
        if (r.payment_type === "rev_share")
            return `${r.rev_share_pct}% rev-share — ${eff.source}`;
        return `$${r.flat_amount} flat — ${eff.source}`;
    })();

    return (
        <Dialog open={!!video} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="bg-white border-slate-200 rounded-2xl max-w-2xl"
                data-testid="ad-kit-modal"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">Download & ad kit</DialogTitle>
                    <DialogDescription>
                        Use this video your way. Castline tracks revenue back via the UTM, promo code or campaign tag below.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="card-ecom p-4 flex items-center justify-between flex-wrap gap-3">
                        <div className="min-w-0">
                            <div className="font-display font-semibold truncate">{video.title}</div>
                            <div className="text-xs text-slate-500 truncate">
                                by {video.creator_name} · {ruleLabel}
                            </div>
                        </div>
                        <a
                            href={`${BACKEND}/api/videos/${video.id}/download`}
                            className="btn-pill-primary inline-flex items-center"
                            data-testid="ad-kit-download"
                        >
                            <Download className="w-4 h-4 mr-1.5" /> Download raw .mp4
                        </a>
                    </div>

                    {kit && (
                        <div className="space-y-2.5">
                            <div className="text-xs text-slate-500">
                                Pick at least one tracking handle and add it to the campaign you create on Meta / Google / Amazon Ads. Castline reads back the performance and pays the creator on your terms.
                            </div>
                            <CopyField
                                label="UTM string · paste on destination URL"
                                value={utmString}
                                testId="kit-utm"
                            />
                            <CopyField
                                label="Promo code · use at checkout"
                                value={kit.promo_code}
                                testId="kit-promo"
                            />
                            <CopyField
                                label="Campaign tag · name your ad campaign with this"
                                value={kit.campaign_tag}
                                testId="kit-tag"
                            />
                        </div>
                    )}

                    <details className="text-xs text-slate-500">
                        <summary className="cursor-pointer text-slate-700 font-medium">
                            How tracking works
                        </summary>
                        <ul className="mt-2 space-y-1 list-disc list-inside">
                            {kit?.instructions?.map((line, i) => <li key={i}>{line}</li>)}
                        </ul>
                    </details>

                    <div className="surface-soft rounded-xl p-4 border hairline" data-testid="test-attribution-block">
                        <div className="flex items-center gap-2 mb-2">
                            <Beaker className="w-4 h-4 text-coral" />
                            <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                                Test attribution
                            </span>
                        </div>
                        <p className="text-xs text-slate-500 mb-3">
                            Simulate $X of attributed revenue and see exactly what the creator would earn under the current terms. No money moves.
                        </p>
                        <div className="flex gap-2 items-center">
                            <div className="flex items-center gap-1.5 surface-warm rounded-xl px-3 h-10 border hairline">
                                <span className="text-slate-400 text-sm">$</span>
                                <input
                                    type="number"
                                    min="1"
                                    step="50"
                                    value={testRevenue}
                                    onChange={(e) => setTestRevenue(e.target.value)}
                                    className="bg-transparent w-24 text-sm focus:outline-none"
                                    data-testid="test-attribution-input"
                                />
                            </div>
                            <button
                                onClick={runTest}
                                disabled={testing}
                                className="btn-pill-primary inline-flex items-center text-sm"
                                data-testid="test-attribution-run"
                            >
                                <TrendingUp className="w-4 h-4 mr-1.5" />
                                {testing ? "Running…" : "Simulate"}
                            </button>
                        </div>
                        {testResult && (
                            <div className="mt-3 grid grid-cols-3 gap-2 text-center" data-testid="test-attribution-result">
                                <div className="surface-warm rounded-lg p-2.5">
                                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Revenue</div>
                                    <div className="font-display font-semibold text-slate-900 mt-0.5">
                                        ${Number(testResult.revenue).toLocaleString()}
                                    </div>
                                </div>
                                <div className="surface-warm rounded-lg p-2.5 ring-1 ring-coral/20">
                                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                                        Creator earns
                                    </div>
                                    <div className="font-display font-semibold text-coral mt-0.5">
                                        ${Number(testResult.creator_payout).toLocaleString()}
                                    </div>
                                </div>
                                <div className="surface-warm rounded-lg p-2.5">
                                    <div className="text-[10px] uppercase tracking-wider text-slate-400">You keep</div>
                                    <div className="font-display font-semibold text-slate-900 mt-0.5">
                                        ${Number(testResult.brand_keeps).toLocaleString()}
                                    </div>
                                </div>
                                {testResult.rule_source ? (
                                    <div className="col-span-3 text-[11px] text-slate-500 mt-1">
                                        Applied {testResult.rule_source} rule ·{" "}
                                        {testResult.rule.payment_type === "rev_share"
                                            ? `${testResult.rule.rev_share_pct}% rev-share`
                                            : `$${testResult.rule.flat_amount} flat`}
                                    </div>
                                ) : (
                                    <div className="col-span-3 text-[11px] text-coral mt-1">
                                        {testResult.note}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function BrandVideos() {
    const [videos, setVideos] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [open, setOpen] = React.useState(null);
    const [view, setView] = React.useState(() => {
        try {
            return localStorage.getItem("castline_brand_videos_view") || "grid";
        } catch (_e) {
            return "grid";
        }
    });
    const [creatorFilter, setCreatorFilter] = React.useState("all");

    const setViewMode = (m) => {
        setView(m);
        try {
            localStorage.setItem("castline_brand_videos_view", m);
        } catch (_e) {
            /* noop */
        }
    };

    const load = async () => {
        try {
            const { data } = await api.get("/brand/videos");
            setVideos(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        load();
    }, []);

    const creators = React.useMemo(() => {
        const map = new Map();
        for (const v of videos) {
            if (v.creator_id && !map.has(v.creator_id)) {
                map.set(v.creator_id, v.creator_name || "Creator");
            }
        }
        return Array.from(map, ([id, name]) => ({ id, name }));
    }, [videos]);

    const filtered = React.useMemo(
        () =>
            creatorFilter === "all"
                ? videos
                : videos.filter((v) => v.creator_id === creatorFilter),
        [videos, creatorFilter],
    );

    if (loading) {
        return (
            <div
                className="card-ecom p-12 text-center text-slate-500"
                data-testid="videos-loading"
            >
                Loading videos…
            </div>
        );
    }

    if (videos.length === 0) {
        return (
            <div className="card-ecom p-12 text-center" data-testid="no-brand-videos">
                <FileVideo className="w-8 h-8 text-coral mx-auto mb-3" />
                <p className="text-slate-600">
                    No videos yet. Invite creators to start building your library.
                </p>
            </div>
        );
    }

    return (
        <>
            {/* Toolbar */}
            <div
                className="flex items-center justify-between flex-wrap gap-3 mb-5"
                data-testid="brand-videos-toolbar"
            >
                <div className="flex items-center gap-2">
                    <select
                        value={creatorFilter}
                        onChange={(e) => setCreatorFilter(e.target.value)}
                        className="rounded-full bg-slate-100 border-0 text-sm px-4 py-2 pr-9 focus:ring-2 focus:ring-coral focus:outline-none"
                        data-testid="creator-filter"
                    >
                        <option value="all">All creators · {videos.length}</option>
                        {creators.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name} ·{" "}
                                {videos.filter((v) => v.creator_id === c.id).length}
                            </option>
                        ))}
                    </select>
                    {creatorFilter !== "all" && (
                        <button
                            onClick={() => setCreatorFilter("all")}
                            className="text-xs text-slate-500 hover:text-slate-700"
                            data-testid="clear-creator-filter"
                        >
                            Clear
                        </button>
                    )}
                </div>
                <div
                    className="flex items-center gap-1 bg-slate-100 rounded-full p-1"
                    data-testid="view-toggle"
                >
                    <button
                        onClick={() => setViewMode("grid")}
                        className={`p-1.5 rounded-full transition-all ${
                            view === "grid"
                                ? "bg-white text-slate-900 shadow-sm"
                                : "text-slate-500"
                        }`}
                        title="Grid"
                        data-testid="view-grid"
                    >
                        <Grid3x3 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setViewMode("list")}
                        className={`p-1.5 rounded-full transition-all ${
                            view === "list"
                                ? "bg-white text-slate-900 shadow-sm"
                                : "text-slate-500"
                        }`}
                        title="List"
                        data-testid="view-list"
                    >
                        <List className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {filtered.length === 0 ? (
                <div
                    className="card-ecom p-10 text-center text-sm text-slate-500"
                    data-testid="no-videos-for-creator"
                >
                    No videos from this creator yet.
                </div>
            ) : view === "grid" ? (
                <div
                    className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
                    data-testid="brand-videos-grid"
                >
                    {filtered.map((v) => (
                        <div
                            key={v.id}
                            className="card-ecom overflow-hidden flex flex-col"
                            data-testid={`brand-video-${v.id}`}
                        >
                            <div className="aspect-video bg-slate-100 relative">
                                {v.thumbnail_url ? (
                                    <img
                                        src={v.thumbnail_url}
                                        alt={v.title}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                                        <FileVideo className="w-10 h-10" />
                                    </div>
                                )}
                                <span className="absolute top-2 left-2 chip chip-lime text-[10px]">
                                    {v.creator_name}
                                </span>
                            </div>
                            <div className="p-4 flex-1 flex flex-col gap-3">
                                <div className="flex-1">
                                    <div className="font-display font-semibold leading-tight">
                                        {v.title}
                                    </div>
                                    {v.description && (
                                        <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                                            {v.description}
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <button
                                        onClick={() => setOpen(v)}
                                        className="btn-pill-primary inline-flex items-center text-sm"
                                        data-testid={`open-ad-kit-${v.id}`}
                                    >
                                        <Megaphone className="w-4 h-4 mr-1.5" /> Download &
                                        ad kit
                                    </button>
                                    <a
                                        href={`${BACKEND}/api/videos/${v.id}/download`}
                                        className="btn-pill-ghost inline-flex items-center text-sm"
                                        data-testid={`quick-download-${v.id}`}
                                        title="Download raw .mp4"
                                    >
                                        <Download className="w-4 h-4" />
                                    </a>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="card-ecom divide-y hairline overflow-hidden" data-testid="brand-videos-list">
                    {filtered.map((v) => (
                        <div
                            key={v.id}
                            className="p-4 flex items-center gap-4 hover:bg-slate-50/60 transition-colors"
                            data-testid={`brand-video-row-${v.id}`}
                        >
                            <div className="w-28 h-16 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                                {v.thumbnail_url ? (
                                    <img
                                        src={v.thumbnail_url}
                                        alt={v.title}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                                        <FileVideo className="w-5 h-5" />
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-semibold truncate">
                                    {v.title}
                                </div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    by{" "}
                                    <span className="text-slate-700 font-medium">
                                        {v.creator_name}
                                    </span>
                                    {v.category && <span> · {v.category}</span>}
                                    {v.duration && <span> · {v.duration}s</span>}
                                </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                <button
                                    onClick={() => setOpen(v)}
                                    className="btn-pill-primary inline-flex items-center text-sm"
                                    data-testid={`open-ad-kit-list-${v.id}`}
                                >
                                    <Megaphone className="w-4 h-4 mr-1.5" /> Ad kit
                                </button>
                                <a
                                    href={`${BACKEND}/api/videos/${v.id}/download`}
                                    className="btn-pill-ghost inline-flex items-center text-sm"
                                    data-testid={`quick-download-list-${v.id}`}
                                    title="Download raw .mp4"
                                >
                                    <Download className="w-4 h-4" />
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <AdKitDialog video={open} onClose={() => setOpen(null)} />
        </>
    );
}
